import type { CSSProperties } from "vue";
import { type RouteLocationRaw } from "vue-router";

import type {
  ChartClickEvent,
  ChartInteraction,
} from "shared/modules/Charts/types/chartInteractions";
import type { Chart } from "shared/modules/Charts/types/chartTypes";
import type {
  ChartLibraryData,
  ChartLibraryProps,
  ChartLibraryRequestChartOptions,
} from "shared/modules/Charts/types/data";
import { type FeatureKey } from "shared/types/features";
import { type PermissionKey } from "shared/types/permissions";

export interface ChartEmptyState {
  button?: {
    label: string;
    to?: RouteLocationRaw;
  };
  buttonTooltip?: () => string;
  description: string;
  image: string;
  isButtonDisabled?: () => boolean;
  style?: CSSProperties;
}

export enum SidebarComponent {
  ChartSettingsStockSymbolForm = "ChartSettingsStockSymbolForm",
  KeyMessagesForm = "ChartSettingsKeyMessagesForm",
  LinkedStreamForm = "ChartSettingsSelectLinkedStreamForm",
  SettingsForm = "ChartSettingsForm",
  WithExportOptionsForm = "ChartSettingsWithExportOptionsForm",
}

export type ChartComponent =
  | "AdvertisingValueEquivalency"
  | "BroadcastTimeByMediaType"
  | "CoverageByMediaType"
  | "CoverageVolumeAndPotentialAudienceReach"
  | "CoverageVolumeAndStockPrice"
  | "CoverageVolumeTimeline"
  | "AIVisibilityDataTable"
  | "DataTableWithChange"
  | "DataTableWithChangeAcrossPeriods"
  | "DesktopVsMobileReadership"
  | "DomainAuthority"
  | "EmojiCount"
  | "FacebookEngagementMetric"
  | "GeographicSpread"
  | "KeyMessages"
  | "KeyPlatformInfluencers"
  | "LeadingSources"
  | "MentionBenchmarkTimeline"
  | "MentionStreamInsightsSummary"
  | "OutreachCoverageTimeline"
  | "OutreachInsightsSummary"
  | "OutreachPerformance"
  | "OutreachTopEngagedContactsTable"
  | "PotentialAudienceReach"
  | "ReactScoreBreakdown"
  | "SentimentBenchmark"
  | "SentimentBreakdown"
  | "SentimentTimeline"
  | "ShareOfVoice"
  | "ShareOfVoiceTimeline"
  | "SocialEngagePerformance"
  | "SocialInsightsSummary"
  | "SocialPostHighlight"
  | "TopArticlesTable"
  | "TopFacebookPages"
  | "TopPlatformPosts"
  | "TotalSocialShares"
  | "VolumeOfCoverageByPosition"
  | "WordCloud";

export interface BaseChartConfig {
  allowsMultipleSocialAccounts?: boolean;
  chart?: Chart;
  chartOptions?: ChartLibraryData;
  comparison?: boolean;
  component: ChartComponent;
  componentAttributes?: ChartLibraryProps;
  emptyState?: ChartEmptyState;
  featureFlag?: FeatureKey | string;
  isBeta?: boolean;
  label?: string;
  legendStyle?: CSSProperties;
  maxSelectedAccounts?: number;
  maxSelectedPosts?: number;
  maxStreams?: number;
  method: string;
  options?: ChartLibraryRequestChartOptions;
  requiredPermissions?: PermissionKey[];
  requiresSocialPostSelection?: boolean;
  requiresStockSymbol?: boolean;
  selectsFirstPostByDefault?: boolean;
  settingsComponent?: SidebarComponent;
  socialAccountSelector?: "instagram_handle" | "organisation";
  style?: CSSProperties;
}

export type ChartConfig<Event = ChartClickEvent> = BaseChartConfig &
  ChartInteraction<Event>;

export type ChartConfigIndex<Event = ChartClickEvent> = Partial<
  Record<Chart, ChartConfig<Event>>
>;

export type ChartConfigType = ChartConfig<never> | ChartConfig<ChartClickEvent>;
